import { arrayLiteralToCamelCase } from 'pos-shared/utils/textHelpers.ts';
import { PhoneValidation } from 'pos-shared/utils/validationHelpers.ts';
import { z } from 'zod';

import { AddressGeoLocationSchema, AddressSchema } from './addressSchema.ts';
import { AuditResponseSchema } from './auditSchema.ts';
import {
  CommunicationMethodChannelEnum,
  CommunicationMethodEnum,
} from './communicationSchema.ts';
import { FleetAccountSchema } from './fleetSchema.ts';
import {
  createPaginatedResponseSchema,
  PagedRequestSchema,
} from './pagingSchema.ts';
import { CustomerTypeEnum } from './statusSchema.ts';

export const CustomerIdSchema = z.object({
  customerId: z.uuid(),
});
export const CustomerTitleEnum = z.enum(['Mr', 'Mrs', 'Miss', 'Ms']);
export const CustomerTitleCamelEnum = z.enum(
  arrayLiteralToCamelCase(CustomerTitleEnum.options)
);

export const CustomerNameFeSchema = z.object({
  title: z.string().nullish(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  fullName: z.string().nullish(),
  initial: z.string().nullish(),
});

export const CustomerMarketingPreferencesSchema = z.object({
  allowInternalMarketing: z.boolean(),
  allow3rdPartyMarketing: z.boolean(),
});

export const CustomerCommunicationPreferenceSchema = z
  .object({
    id: z.uuid().optional(),
    isCurrent: z.boolean().optional(),
  })
  .extend(CustomerMarketingPreferencesSchema.shape);

export const UpdateCustomerMarketingPreferencesRequestSchema =
  CustomerMarketingPreferencesSchema.extend({
    customerId: z.uuid(),
  });

export const CustomerOrderCommunicationPreferencesSchema = z.object({
  email: z.boolean(),
  phone: z.boolean(),
});

export const CustomerCommunicationMethodChannelSchema = z.object({
  type: CommunicationMethodEnum,
  channel: CommunicationMethodChannelEnum,
  value: z.string().nullish(),
});

export const CustomerCommunicationMethodSchema =
  CustomerCommunicationMethodChannelSchema.extend({
    id: z.uuid(),
    isDefault: z.boolean(),
  });

export const CustomerUpdateCommunicationMethodSchema =
  CustomerCommunicationMethodSchema.extend({
    isDeleted: z.boolean().optional(),
  });

const CustomerCommunicationPreferencesRefinedSchema =
  CustomerMarketingPreferencesSchema.extend({
    email: z.boolean(),
    phone: z.boolean(),
  });

export const CustomerCommunicationsPreferencesFormSchema = z.object({
  communicationPreferences: CustomerCommunicationPreferencesRefinedSchema,
});

export const CustomerCommunicationMethodDefaults = z.object({
  email: z.boolean(),
  phone: z.boolean(),
  additionalPhone: z.boolean(),
});

export const CustomerLoyaltyProgramSchema = z.object({
  affiliationName: z.string().nullish(),
  membershipReference: z.string().nullish(),
});

export const CustomerLoyaltyProgramFormSchema = z.object({
  loyaltyProgram: CustomerLoyaltyProgramSchema.nullish(),
});

export const UpdateCustomerLoyaltyProgramRequestSchema =
  CustomerLoyaltyProgramFormSchema.extend({
    customerId: z.uuid(),
  });

export const CreateCustomerFeSchema = z.object({
  title: z.string().min(1).nullish(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  businessName: z.string().optional(),
  businessNumber: z.string().optional(),
  initial: z.string().min(1).nullish(),
  email: z.email(),
  additionalEmail: z.union([z.email(), z.literal('')]).nullish(),
  phone: z.string().regex(PhoneValidation, 'Invalid Phone Number'),
  additionalPhone: z.string().nullish(),
  employeeId: z.string().nullish(),
  communicationPreference: CustomerMarketingPreferencesSchema,
  communicationMethodDefaults: CustomerCommunicationMethodDefaults,
  taxExemptionReference: z.string().nullish(),
  loyaltyProgram: CustomerLoyaltyProgramSchema.optional(),
});

export const GetCustomerFeRequestSchema = z.object({
  customerId: z.uuid(),
  orderId: z.uuid().optional(),
  orderStatus: z.string().optional(),
});

export const SearchCustomerEmailAddressesFeRequestSchema = z.object({
  searchText: z.string().optional(),
  customerId: z.uuid(),
});

export const SearchCustomerEmailAddressesFeResponseSchema = z.object({
  results: z.array(
    z.object({
      id: z.uuid(),
      email: z.string(),
    })
  ),
});

export const CustomerAddressFeSchema = z.object({
  id: z.uuid(),
  isDefault: z.boolean(),
  type: z.string(),
  addressLine1: z.string().min(1),
  addressLine2: z.string().nullish(),
  addressLine3: z.string().nullish(),
  addressLine4: z.string().nullish(),
  postalCode: z.string().min(1),
  country: z.string().nullish(),
  countryCode: z.string().nullish(),
  timezone: z.string().nullish(),
  geoLocation: AddressGeoLocationSchema,
});

export const GetCustomerAddressSchema = CustomerAddressFeSchema.omit({
  postalCode: true,
  type: true,
}).extend({
  type: z.string().nullish(),
  addressLine1: z.string().nullish(),
  summary: z.string().nullish(),
  postalCode: z.string().nullish(),
});

export const UpdateCustomerAddressSchema = z.object({
  addressId: z.uuid(),
  type: z.string().nullish(),
  isDefault: z.boolean(),
  summary: z.string().nullish(),
  addressLine1: z.string().nullish(),
  addressLine2: z.string().nullish(),
  addressLine3: z.string().nullish(),
  addressLine4: z.string().nullish(),
  postalCode: z.string().nullish(),
  country: z.string().nullish(),
  countryCode: z.string().nullish(),
  timezone: z.string().nullish(),
  geoLocation: AddressGeoLocationSchema,
});

export const UpdateCustomerAddressRequestSchema = z.object({
  customerId: z.uuid(),
  address: UpdateCustomerAddressSchema,
});

export const AddCustomerAddressFeSchema = CustomerAddressFeSchema.omit({
  id: true,
}).extend({
  addressLine3: z.string().min(1),
  addressLine4: z.string().min(1),
});

export const CreateOrderCustomerFeSchema = CreateCustomerFeSchema.omit({
  communicationPreference: true,
  communicationMethodDefaults: true,
}).extend({
  address: AddCustomerAddressFeSchema,
  communicationPreferences: CustomerCommunicationPreferencesRefinedSchema,
});

export const GetCustomerAddressFeSchema = z.object({
  addresses: CustomerAddressFeSchema.array(),
});

export const DeleteCustomerAddressSchema = z.object({
  customerId: z.uuid(),
  addressId: z.uuid(),
});

export const CustomerTaxExemptionReferenceSchema = z.object({
  reference: z.string().nullish(),
  type: z.string().nullish(),
});

export const CustomerFederalTaxExemptionUpdateSchema = z.object({
  isFetExempt: z.boolean(),
  customerId: z.string(),
});

export const CustomerFederalTaxExemptionResponseSchema = z.object({
  isFetExempt: z.boolean(),
});

export const CustomerAddressTypeEnum = z.enum([
  'Fulfilment',
  'Billing',
  'Work',
  'Both',
]);

export const AddCustomerCommunicationMethodSchema = z.object({
  customerId: z.uuid(),
  communicationMethod: CustomerCommunicationMethodSchema.omit({ id: true }),
});

export const UpdateCustomerCommunicationMethodRequestSchema = z.object({
  customerId: z.uuid(),
  communicationMethods: z
    .array(CustomerUpdateCommunicationMethodSchema)
    .nullish(),
});

const CustomerAddressesSchema = CustomerAddressFeSchema.omit({
  addressLine1: true,
  type: true,
  postalCode: true,
  geoLocation: true,
}).extend({
  type: CustomerAddressTypeEnum,
  addressLine1: z.string().nullish(),
  postalCode: z.string().nullish(),
  geoLocation: z
    .object({
      lat: z.string().nullish(),
      lng: z.string().nullish(),
    })
    .nullish(),
});

export const CfnaApplicationStatusEnum = z.enum([
  'Approved',
  'Declined',
  'Pending',
]);

export const CfnaSettingsSchema = z.object({
  isCfnaCardHolder: z.boolean().nullish(),
  applicationStatus: CfnaApplicationStatusEnum.nullish(),
  applicationDate: z.coerce.date<Date>().nullish(),
  applicationToken: z.string().nullish(),
});

export const CustomerDetailsFeSchema = z
  .object({
    customerId: z.uuid(),
    customerReference: z.string().min(1),
    customerType: CustomerTypeEnum,
    fullName: z.string().optional(),
    businessName: z.string().optional(),
    businessNumber: z.string().optional(),
    email: z.string().optional(),
    additionalEmail: z.union([z.email(), z.literal('')]).optional(),
    phone: z.string().optional(), // Will add back once backend returns valid numbers .regex(PhoneValidation, 'Invalid Phone Number').optional(),
    additionalPhone: z.string().optional(),
    marketingPreferences: CustomerMarketingPreferencesSchema,
    linkedCustomerIds: z.uuid().array().optional(),
    billingAddress: AddressSchema.nullish(),
    fleetAccount: FleetAccountSchema.nullish(),
    orderId: z.uuid().optional(), // if update is related to a specific order
    fulfilmentAddress: AddressSchema.nullish(),
    fulfilmentTo: z.string().array(),
    taxExemption: z.object({
      reference: z.string().nullish(),
      type: z.string().nullish(),
      isRedeemed: z.boolean().nullish(),
    }),
    taxExemptionReference: z.string().nullish(),
    taxExemptionType: z.string().nullish(),
    flyBuys: z.string().nullish(),
    isFetExempt: z.boolean().optional(),
    addresses: CustomerAddressesSchema.array(),
    communicationPreferences: CustomerCommunicationPreferenceSchema.array(),
    communicationMethods: CustomerCommunicationMethodSchema.array(),
    communicationMethodDefaults: CustomerCommunicationMethodDefaults,
    cfna: CfnaSettingsSchema.nullish(),
    additionalProperties: z.object({}).catchall(z.unknown()).optional(),
    loyaltyProgram: CustomerLoyaltyProgramSchema.nullish(),
    employeeId: z.string().nullish(),
  })
  .extend(CustomerNameFeSchema.shape);

export const CustomerSearchRequestFeSchema = z
  .object({
    searchText: z.coerce.string<string>().optional(),
    emailAddress: z.coerce.string<string>().nullish(),
  })
  .extend(PagedRequestSchema.shape);

export const CustomerSearchResultFeSchema = z.object({
  customerId: z.uuid(),
  customerReference: z.string().min(1).optional(),
  name: z.string().optional(),
  email: z.string().nullish(),
  phone: z.string().optional(), // Add regex once backend returns valid numbers .regex(PhoneValidation, 'Invalid Phone Number').optional(),
  vehicles: z.array(z.string()).optional(),
  postcode: z.string().optional(),
});
export const CustomerSearchResponseFeSchema = createPaginatedResponseSchema(
  CustomerSearchResultFeSchema
);

export const CustomerDetailsCreateOrderFeSchema = z.object({
  customerId: z.uuid(),
  name: z.string(),
  email: z.string().optional(),
  phone: z.string().optional(),
  postcode: z.string().optional(),
});

export const AddCustomerCommunicationMethodResponseSchema = z.object({
  communicationMethodId: z.uuid(),
});

export const LinkCustomersRequestSchema = z.object({
  firstCustomerId: z.uuid(),
  secondCustomerId: z.uuid(),
});

export const UnlinkCustomerRequestSchema = z.object({
  customerId: z.uuid(),
  linkedCustomerIds: z.uuid().array(),
});

export const GetCustomerAuditResponseSchema = z.object({
  customerId: z.uuid(),
  audit: AuditResponseSchema,
});

export const AdditionalCustomerPropertiesSchema = z.object({
  customerId: z.string(),
  key: z.string(),
  value: z.string(),
});

export const UpdateOrderCustomerDetailsSchema = z.object({
  orderId: z.uuid(),
  customerId: z.uuid(),
  title: z.string().nullish(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  initial: z.string().nullish(),
  employeeId: z.string().nullish(),
  text: z.string().nullish(),
  addressLine1: z.string().nullish(),
  addressLine2: z.string().nullish(),
  addressLine3: z.string().nullish(),
  addressLine4: z.string().nullish(),
  postalCode: z.string().nullish(),
  country: z.string().nullish(),
  countryCode: z.string().nullish(),
  timeZone: z.string().nullish(),
  geoLocation: z
    .object({
      latitude: z.string().nullish(),
      longitude: z.string().nullish(),
    })
    .nullish(),
  allowInternalMarketing: z.boolean().nullish(),
  allow3rdPartyMarketing: z.boolean().nullish(),
  channels: z.string().array().nullish(),
  chanelsUsageMode: z.string().nullish(),
  communicationMethodDefaults: CustomerCommunicationMethodDefaults.nullish(),
  changeCustomerDetails: z.boolean(),
  phone: z.string().nullish(),
  email: z.email().nullish(),
  additionalEmail: z.union([z.email(), z.literal('')]).optional(),
  additionalPhone: z.string().nullish(),
});

///// TYPES...
export type CustomerIdModel = z.infer<typeof CustomerIdSchema>;
export type CreateCustomerFeModel = z.infer<typeof CreateCustomerFeSchema>;
export type CustomerNameFeModel = z.infer<typeof CustomerNameFeSchema>;
export type GetCustomerFeRequestModel = z.infer<
  typeof GetCustomerFeRequestSchema
>;
export type CustomerSearchRequestModel = z.infer<
  typeof CustomerSearchRequestFeSchema
>;
export type CustomerAddressType = z.infer<typeof CustomerAddressTypeEnum>;
export type CustomerAddressFeModel = z.infer<typeof CustomerAddressFeSchema>;
export type AddCustomerAddressFeModel = z.infer<
  typeof AddCustomerAddressFeSchema
>;
export type UpdateCustomerAddressModel = z.infer<
  typeof UpdateCustomerAddressSchema
>;
export type UpdateCustomerAddressRequestModel = z.infer<
  typeof UpdateCustomerAddressRequestSchema
>;

export type CustomerCommunicationMethodChanelModel = z.infer<
  typeof CustomerCommunicationMethodChannelSchema
>;
export type CustomerCommunicationMethodModel = z.infer<
  typeof CustomerCommunicationMethodSchema
>;
export type CustomerUpdateCommunicationMethodModel = z.infer<
  typeof CustomerUpdateCommunicationMethodSchema
>;
export type AddCustomerCommunicationMethodModel = z.infer<
  typeof AddCustomerCommunicationMethodSchema
>;
export type UpdateCustomerCommunicationMethodRequestModel = z.infer<
  typeof UpdateCustomerCommunicationMethodRequestSchema
>;
export type GetCustomerAddressFeModel = z.infer<
  typeof GetCustomerAddressFeSchema
>;
export type GetCustomerAddressModel = z.infer<typeof GetCustomerAddressSchema>;
export type DeleteCustomerAddressModel = z.infer<
  typeof DeleteCustomerAddressSchema
>;
export type CustomerDetailsFeModel = z.infer<typeof CustomerDetailsFeSchema>;
export type CustomerSearchResponseFeModel = z.infer<
  typeof CustomerSearchResponseFeSchema
>;
export type CustomerSearchResultFeModel = z.infer<
  typeof CustomerSearchResultFeSchema
>;
export type CustomerMarketingPreferencesFeModel = z.infer<
  typeof CustomerMarketingPreferencesSchema
>;
export type UpdateCustomerMarketingPreferencesRequestModel = z.infer<
  typeof UpdateCustomerMarketingPreferencesRequestSchema
>;
export type UpdateCustomerLoyaltyProgramRequestModel = z.infer<
  typeof UpdateCustomerLoyaltyProgramRequestSchema
>;
export type CustomerOrderCommunicationPreferencesModel = z.infer<
  typeof CustomerOrderCommunicationPreferencesSchema
>;
export type CustomerCommunicationsPreferencesFormModel = z.infer<
  typeof CustomerCommunicationsPreferencesFormSchema
>;
export type CustomerLoyaltyProgramModel = z.infer<
  typeof CustomerLoyaltyProgramSchema
>;
export type CustomerLoyaltyProgramFormModel = z.infer<
  typeof CustomerLoyaltyProgramFormSchema
>;
export type CustomerDetailsCreateOrderFeModel = z.infer<
  typeof CustomerDetailsCreateOrderFeSchema
>;
export type CreateOrderCustomerFeModel = z.infer<
  typeof CreateOrderCustomerFeSchema
>;
export type CustomerCommunicationMethodsModel = z.infer<
  typeof CustomerCommunicationMethodSchema
>;

export type AddCustomerCommunicationMethodResponseModel = z.infer<
  typeof AddCustomerCommunicationMethodResponseSchema
>;

export type CustomerTaxExemptionReferenceModel = z.infer<
  typeof CustomerTaxExemptionReferenceSchema
>;
export type CustomerFederalTaxExemptionUpdateModel = z.infer<
  typeof CustomerFederalTaxExemptionUpdateSchema
>;

export type CustomerFederalTaxExemptionResponseModel = z.infer<
  typeof CustomerFederalTaxExemptionResponseSchema
>;

export type LinkCustomersRequestModel = z.infer<
  typeof LinkCustomersRequestSchema
>;

export type CfnaSettingsSchemaModel = z.infer<typeof CfnaSettingsSchema>;

export type UnlinkCustomerRequestModel = z.infer<
  typeof UnlinkCustomerRequestSchema
>;
export type GetCustomerAuditResponseModel = z.infer<
  typeof GetCustomerAuditResponseSchema
>;

export type AdditionalCustomerPropertiesModel = z.infer<
  typeof AdditionalCustomerPropertiesSchema
>;

export type UpdateOrderCustomerDetailsModel = z.infer<
  typeof UpdateOrderCustomerDetailsSchema
>;
